import { t } from 'ttag';

import {
  CustomPrintingErrorCode,
  PrinterErrorCategory,
  PrinterErrorCodes,
  PrinterOpenChannelErrorCode,
  PrinterPrintErrorCode,
  PrinterSearchErrorCode,
} from './definitions';

// Disable deepsource's warning about cyclomatic complexity, as this is a straight-forward switch
// skipcq: JS-R1005
export const getErrorCategory = (errorCode: PrinterErrorCodes): PrinterErrorCategory | null => {
  switch (errorCode) {
    // These are not actually errors, and should never reach here
    case PrinterSearchErrorCode.PrinterSearchErrorNoError:
    case PrinterOpenChannelErrorCode.OpenChannelErrorCodeNoError:
    case PrinterPrintErrorCode.PrintErrorCodeNoError:
      return null;

    case PrinterOpenChannelErrorCode.OpenChannelErrorCodeOpenStreamFailure:
    case PrinterOpenChannelErrorCode.OpenChannelErrorCodeTimeout:
    case PrinterPrintErrorCode.PrintErrorCodeChannelErrorUnsupportedChannel:
    case CustomPrintingErrorCode.FailedToConnectToPrinter:
    case CustomPrintingErrorCode.FailedToGetPrintSettings:
    case CustomPrintingErrorCode.InvalidPrinter:
    case CustomPrintingErrorCode.MissingParameters:
      return PrinterErrorCategory.UnableToConnectToPrinter;

    case PrinterPrintErrorCode.PrintErrorCodePDFPageError:
    case PrinterPrintErrorCode.PrintErrorCodeFilepathURLError:
    case PrinterPrintErrorCode.PrintErrorCodeUnsupportedFile:
    case CustomPrintingErrorCode.DownloadBadgeFailed:
      return PrinterErrorCategory.UnableToGenerateBadge;

    case PrinterPrintErrorCode.PrintErrorCodeDataBufferError:
    case PrinterPrintErrorCode.PrintErrorCodeChannelTimeout:
    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorCommunicationError:
    case PrinterPrintErrorCode.PrintErrorCodeChannelErrorStreamStatusError:
      return PrinterErrorCategory.PrinterNetwork;

    case PrinterPrintErrorCode.PrintErrorCodeCanceled:
      return PrinterErrorCategory.PrintCancelled;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorBusy:
      return PrinterErrorCategory.BusyError;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorCoverOpen:
      return PrinterErrorCategory.PrinterCoverOpen;

    case PrinterPrintErrorCode.PrintErrorCodeSetMarginError:
    case PrinterPrintErrorCode.PrintErrorCodeSetLabelSizeError:
    case PrinterPrintErrorCode.PrintErrorCodeCustomPaperSizeError:
      return PrinterErrorCategory.PageConfiguration;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorPaperEmpty:
    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorPaperJam:
    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorMediaCannotBeFed:
      return PrinterErrorCategory.PaperError;

    case PrinterPrintErrorCode.PrintErrorCodeSetLengthError:
    case PrinterPrintErrorCode.PrintErrorCodeTubeSettingError:
    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorExpansionBufferFull:
    case PrinterPrintErrorCode.PrintErrorCodeUnknownError:
      return PrinterErrorCategory.UnknownError;

    // The next two aren't unknown, but we're not using templates and should never encounter them
    case PrinterPrintErrorCode.PrintErrorCodeTemplatePrintNotSupported:
    case PrinterPrintErrorCode.PrintErrorCodeInvalidTemplateKey:
      return PrinterErrorCategory.UnknownError;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorPrinterTurnedOff:
      return PrinterErrorCategory.PrinterOffline;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorBatteryWeak:
      return PrinterErrorCategory.PrinterLowBattery;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorOverHeat:
      return PrinterErrorCategory.PrinterOverheated;

    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorHighVoltageAdapter:
    case PrinterPrintErrorCode.PrintErrorCodeUnsupportedCharger:
      return PrinterErrorCategory.PrinterPower;

    case PrinterPrintErrorCode.PrintErrorCodePrintSettingsError:
    case PrinterPrintErrorCode.PrintErrorCodePrintSettingsNotSupportError:
    case PrinterPrintErrorCode.PrintErrorCodePrinterModelError:
    case PrinterPrintErrorCode.PrintErrorCodeSetModelError:
    case PrinterPrintErrorCode.PrintErrorCodeIncompatibleOptionalEquipment:
      return PrinterErrorCategory.PrinterConfiguration;

    // This would only show up on screens like printer settings, as opposed to the print flow
    case PrinterSearchErrorCode.PrinterSearchErrorAlreadySearching:
    case PrinterSearchErrorCode.PrinterSearchErrorCanceled:
    case PrinterSearchErrorCode.PrinterSearchErrorUnsupported:
    case PrinterSearchErrorCode.PrinterSearchErrorUnknownError:
    case PrinterPrintErrorCode.PrintErrorCodePrinterStatusErrorUnknownError:
      return PrinterErrorCategory.SearchError;

    case CustomPrintingErrorCode.CheckInFailed:
      return PrinterErrorCategory.CheckInFailed;

    case CustomPrintingErrorCode.UnableToGenerateBadge:
      return PrinterErrorCategory.UnableToGenerateBadge;

    default: {
      // Guard to ensure we never forget to handle a new error category
      const exhaustiveCheck: never = errorCode;
      // eslint-disable-next-line no-console
      console.error(new Error(`Unhandled error code case: ${exhaustiveCheck}`));
      return PrinterErrorCategory.UnknownError;
    }
  }
};

export const getErrorCategoryMessage = (errorCategory: PrinterErrorCategory): string => {
  const errorCategoryMessages: Record<PrinterErrorCategory, string> = {
    [PrinterErrorCategory.UnableToConnectToPrinter]: t`Unable to connect to printer`,
    [PrinterErrorCategory.UnableToGenerateBadge]: t`Unable to generate badge`,
    [PrinterErrorCategory.PrinterNetwork]: t`Network error - unable to send badge to printer`,
    [PrinterErrorCategory.PrintCancelled]: t`Print job cancelled at printer`,
    [PrinterErrorCategory.BusyError]: t`Printer is busy with another print job`,
    [PrinterErrorCategory.PrinterCoverOpen]: t`Printer cover is open`,
    [PrinterErrorCategory.PageConfiguration]: t`Page configuration error`,
    [PrinterErrorCategory.PaperError]: t`Paper error - check paper`,
    [PrinterErrorCategory.UnknownError]: t`Unknown error`,
    [PrinterErrorCategory.PrinterOffline]: t`Printer is offline`,
    [PrinterErrorCategory.PrinterLowBattery]: t`Printer battery is low`,
    [PrinterErrorCategory.PrinterOverheated]: t`Printer has overheated - please wait for printer to cool down`,
    [PrinterErrorCategory.PrinterPower]: t`Printer has detected an unsupported power adapter`,
    [PrinterErrorCategory.PrinterConfiguration]: t`Printer configuration error`,
    [PrinterErrorCategory.SearchError]: t`Error searching for printers`,
    [PrinterErrorCategory.CheckInFailed]: t`Check-in failed`,
  };
  return errorCategoryMessages[errorCategory] ?? t`Unknown error`;
};

export const canErrorBeRetried = (errorCategory: PrinterErrorCategory): boolean => {
  switch (errorCategory) {
    case PrinterErrorCategory.UnableToConnectToPrinter:
    case PrinterErrorCategory.UnableToGenerateBadge:
    case PrinterErrorCategory.PrinterNetwork:
    case PrinterErrorCategory.PrintCancelled:
    case PrinterErrorCategory.BusyError:
      return true;

    default:
      return false;
  }
};
